import { inject, NewInstance, bindable, bindingMode, computedFrom, BindingEngine, TaskQueue } from 'aurelia-framework';
import { EventAggregator } from 'aurelia-event-aggregator';
import { DialogController } from 'aurelia-dialog';
import { DialogService } from 'aurelia-dialog';
import { MaterialService } from 'pages/system-fields/materials/service';
import { CoatingService } from 'pages/system-fields/coatings/service';
import { UnitOfMeasurementService } from 'pages/system-fields/unit-of-measurements/service';
import { toParsedFloat } from 'resources/utility';
import { BindingSignaler } from 'aurelia-templating-resources';
import { QuoteService } from 'pages/quotes/service';
import moment from 'moment';

@inject(DialogController, EventAggregator, DialogService, MaterialService, CoatingService, UnitOfMeasurementService, QuoteService, BindingSignaler, BindingEngine, TaskQueue)
export class RawMaterialEdit {
    @bindable rawMaterial;
    @bindable quote;

    constructor(controller, ea, dialogService, materialService, coatingService, unitOfMeasurementService, quoteService, bindingSignaler, bindingEngine, taskQueue) {
        this.controller = controller;
        this.ea = ea;
        this.dialogService = dialogService;
        this.materialService = materialService;
        this.coatingService = coatingService;
        this.unitOfMeasurementService = unitOfMeasurementService;
        this.quoteService = quoteService;
        this.showEdit = false;
        this.signaler = bindingSignaler;
        this.taskQueue = taskQueue;

        this.quote = {};
        this.bindingEngine = bindingEngine;

        this.ea.subscribe('quote:reload', () => {
          this.taskQueue.queueMicroTask(async () => {
            await this.loadRawMaterial();
          });
        });
    }

    loadRawMaterial = async () => {
      if (this.rawMaterial != null) {
        this.showEdit = true;
        await this.getSpecificGravities();
        this.changeThickness();
        if (this.rawMaterial.sgaFreeze) {
          this.rawMaterial.sgaFreezeOverride = this.rawMaterial.sgaOverride;
          // let result = (this.netPrice * this.rawMaterial.sgaPercent) / 100;
          // this.rawMaterial.sgaFreezeOverride = toParsedFloat(result);
        }
        if (this.rawMaterial.profitFreeze) {
          this.rawMaterial.profitFreezeOverride = this.rawMaterial.profitOverride;
          // let result = ((Number(this.netPrice) + Number(this.rawMaterial.sgaFreezeOverride)) * this.rawMaterial.profitPercent) / 100;
          // this.rawMaterial.profitFreezeOverride = toParsedFloat(result);          
        }

        if (this.rawMaterial.scrapUnitPriceOverride == null || this.rawMaterial.scrapUnitPriceOverride == 0) {
          this.rawMaterial.scrapUnitPriceOverride = this.quote.scrapUnitPrice;
        }

        this.bindingEngine.propertyObserver(this.rawMaterial, 'coatingId').subscribe(async (newValue) => {
          if (newValue != null) await this.getSpecificGravities();
        });
      }
    };

    bind() {
      this.name = window.localStorage.getItem('name');
      if ((this.quote.lockedAt != null && this.quote.lockedBy.toLowerCase() != this.name.toLowerCase() && moment().utc().diff(moment(this.quote.lockedAt), 'minutes') < 20) || this.quote.isSnapshot) {
        this.isReadonly = true;
      }
    }

    async attached() {
      await this.loadRawMaterial();
    }

    addRawMaterial() {
      this.rawMaterial = {
        gravity : 7.85,
        sgaPercent: this.quote.sgaPercent,
        profitPercent: this.quote.profitPercent,
        sgaFreezeOverride: 0,
        profitFreezeOverride: 0,
        sgaOverride: 0,
        profitOverride: 0,
        variance: 0,
        scrapUnitPriceOverride: this.quote.scrapUnitPrice
      };
      this.showEdit = true;
      this.specificGravities = [];
      this.bindingEngine.propertyObserver(this.rawMaterial, 'coatingId').subscribe(async (newValue) => {
        if (newValue != null) await this.getSpecificGravities();
      });
    }

    addMaterial() {
      this.dialogService.open({ viewModel: this.materialService.editor });
    }

    addCoating() {
      this.dialogService.open({ viewModel: this.coatingService.editor });
    }

    addUnitOfMeasurement () {
      this.dialogService.open({ viewModel: this.unitOfMeasurementService.editor });
    }

    @computedFrom('rawMaterial.blankLength', 'rawMaterial.blankWidth', 'rawMaterial.thickness', 'rawMaterial.gravity', 'rawMaterial.numberOut')
    get grossWeight() {
      if (this.rawMaterial != undefined) {
        let result = 
          (toParsedFloat(this.rawMaterial.blankLength) * 
            toParsedFloat(this.rawMaterial.blankWidth) * 
            toParsedFloat(this.rawMaterial.thickness) * 
            toParsedFloat(this.rawMaterial.gravity)) / toParsedFloat(this.rawMaterial.numberOut) / 1000000;
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }

    get scrapWeight() {
      if (this.rawMaterial != undefined) {
        let result = toParsedFloat(this.grossWeight) - toParsedFloat(this.rawMaterial.netWeight);
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }

    get yeild() {
      if (this.rawMaterial != undefined && this.grossWeight > 0 && this.rawMaterial.netWeight > 0) {
        let result = (toParsedFloat(this.rawMaterial.netWeight) / toParsedFloat(this.grossWeight)) * 100;
        return toParsedFloat(result, 2);
      }
      return toParsedFloat(0);
    }

    @computedFrom('rawMaterial.scrapUnitPriceOverride', 'scrapWeight')
    get scrapAllowance() {
      if (this.rawMaterial != undefined) {
        let result = toParsedFloat(this.scrapWeight) * toParsedFloat(this.rawMaterial.scrapUnitPriceOverride);
        return toParsedFloat(result);  
      }
      return toParsedFloat(0);
    }

    get subtotal() {
      if (this.rawMaterial != undefined) {
        let result = toParsedFloat(this.rawMaterial.price) * this.grossWeight;
        this.rawMaterial.subtotal = result;
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }

    get netPrice() {
      if (this.rawMaterial != undefined) {
        let result = this.subtotal - this.scrapAllowance;
        this.rawMaterial.netPrice = result;
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }

    get sga() {
      if (this.rawMaterial != undefined) {
        // if (this.rawMaterial.sgaOverride != 0) {
        //   return this.rawMaterial.sgaOverride;
        // }
        let result = (this.netPrice * this.rawMaterial.sgaPercent) / 100;
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }

    get profit() {
      if (this.rawMaterial != undefined) {
        // if (this.rawMaterial.profitOverride != 0) {
        //   return this.rawMaterial.profitOverride;
        // }
        let result = ((Number(this.netPrice) + Number(this.sga)) * this.rawMaterial.profitPercent) / 100;
        return toParsedFloat(result);
      }
      return toParsedFloat(0);
    }


    get variance() {
      if (this.rawMaterial != undefined) {
        if (!this.rawMaterial.sgaFreeze && !this.rawMaterial.profitFreeze) {
          return toParsedFloat(0.0000);
        }
        const newSGA = this.rawMaterial.sgaFreeze ? Number(this.rawMaterial.sgaOverride) : Number(this.sga);
        const newProfit = this.rawMaterial.profitFreeze ? Number(this.rawMaterial.profitOverride) : Number(this.profit);
        let result = (newSGA + newProfit) - (Number(this.profit) + Number(this.sga));
        this.rawMaterial.variance = result;
        return toParsedFloat(result);
      }
      return toParsedFloat(0.0000);
    }

    get sgaPercent() {
      if (this.rawMaterial != undefined) {
        return toParsedFloat(this.rawMaterial.sgaPercent);
      }
    }

    get profitPercent() {
      if (this.rawMaterial != undefined) {
        return toParsedFloat(this.rawMaterial.profitPercent);
      }
    }

    freezeProfit(e) {
      if (this.rawMaterial.profitFreeze) {
        this.rawMaterial.profitFreezeOverride = this.profit;
        this.rawMaterial.profitOverride = this.rawMaterial.profitFreezeOverride;
      }
      else {
        this.rawMaterial.profitFreezeOverride = 0;
        this.rawMaterial.profitOverride = this.profit
      }
      this.signaler.signal('freeze-change');
    }

    freezeSGA(e) {
      if (this.rawMaterial.sgaFreeze) {
        this.rawMaterial.sgaFreezeOverride = this.sga;
        this.rawMaterial.sgaOverride = this.rawMaterial.sgaFreezeOverride;
      }
      else {
        this.rawMaterial.sgaFreezeOverride = 0;
        this.rawMaterial.sgaOverride = this.sga;
      }
      this.signaler.signal('freeze-change');
    }

    async getSpecificGravities() {
      this.specificGravities = await this.quoteService.getGravitiesByCoatingId(this.rawMaterial.coatingId);
    }

    changeSga(item) {
      this.rawMaterial.sgaFreezeOverride = this.rawMaterial.sgaOverride = toParsedFloat(item.target.value);
    }

    changeProfit(item) {
      this.rawMaterial.profitFreezeOverride = this.rawMaterial.profitOverride = toParsedFloat(item.target.value);
    }

    changeScrapUnitPrice(item) {
      if (item.target.value == 0 || item.target.value == '') {
        this.rawMaterial.scrapUnitPriceOverride = this.quote.scrapUnitPrice;
      }
    }

    changeThickness() {
      const specificGravity = this.specificGravities.find(x => x.thickness == this.rawMaterial.thickness);
      if (specificGravity) {
        this.rawMaterial.gravity = specificGravity.gravity;
      }
    }

    delete() {
      if (this.quote.rawMaterialId != null && this.quote.rawMaterialId != 0) {
        console.log("this.quote", this.quote);
      }
      else {
        this.rawMaterial = {};
        this.showEdit = false;
      }
    }

}
